import * as React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandEmpty } from "@/components/ui/command";

/**
 * Type-to-filter dropdown that also lets the user commit whatever they
 * typed even when it isn't in `options` — used for Category / Vendor
 * fields where the preset list is a helper, not a hard constraint.
 */
export const Combobox = ({
  value,
  onChange,
  options = [],
  placeholder = "Select or type…",
  emptyText = "No matches.",
  testId,
  className,
  allowCustom = true,
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, search]);

  const exactMatch = options.some((o) => o.toLowerCase() === search.trim().toLowerCase());
  const showCreate = allowCustom && !!search.trim() && !exactMatch;

  const pick = (v) => {
    onChange(v);
    setSearch("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal rounded-sm h-9 hover:bg-primary/10 hover:text-foreground hover:border-primary/40 data-[state=open]:border-primary data-[state=open]:ring-1 data-[state=open]:ring-primary/30",
            !value && "text-muted-foreground",
            className,
          )}
          data-testid={testId}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type to search or add new…"
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            {filtered.length === 0 && !showCreate && (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            <CommandGroup>
              {filtered.map((o) => (
                <CommandItem
                  key={o}
                  value={o}
                  onSelect={() => pick(o)}
                  className="data-[selected=true]:bg-primary data-[selected=true]:text-primary-foreground"
                  data-testid={testId ? `${testId}-option-${o}` : undefined}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === o ? "opacity-100" : "opacity-0")} />
                  {o}
                </CommandItem>
              ))}
              {showCreate && (
                <CommandItem
                  value={`__create__${search}`}
                  onSelect={() => pick(search.trim())}
                  className="data-[selected=true]:bg-primary data-[selected=true]:text-primary-foreground"
                  data-testid={testId ? `${testId}-create` : undefined}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Use "{search.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
